/* eslint-disable react-refresh/only-export-components */
/**
 * Inline Solomon Response Component
 *
 * Shows AI responses directly within the search palette for quick questions.
 * Users can expand to the full Solomon panel for longer conversations.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Loader2, MessageSquare, ExternalLink, RefreshCw, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { toast } from "sonner";
import { COPY_FEEDBACK_TIMEOUT_MS } from "@/lib/timing-constants";
import { solomonErrorMessage } from "@/lib/solomon-error";
import { useFeatureGate } from "@/hooks/useTierGate";
import { FeatureTierGate } from "@/components/ui/TierGate";
import { useOrganization } from "@/hooks/useOrganization";

interface InlineSolomonResponseProps {
  query: string;
  onExpand?: () => void;
  onClear?: () => void;
  onAsk?: (question: string) => void;
  className?: string;
}

interface SolomonMessage {
  role: "user" | "assistant";
  content: string;
}

export function InlineSolomonResponse({
  query,
  onExpand,
  onAsk,
  className,
}: InlineSolomonResponseProps) {
  const [response, setResponse] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [hasAsked, setHasAsked] = useState(false);
  const requestIdRef = useRef(0);
  const location = useLocation();
  const { organizationId } = useOrganization();
  // Solomon AI is Flourish-tier — gate the inline EF call for lower tiers.
  const { hasFeature: solomonEnabled } = useFeatureGate("solomon_ai");

  // Auto-ask when query changes and is long enough
  const askSolomon = useCallback(async () => {
    if (!solomonEnabled || !organizationId || !query.trim() || query.length < 3) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsLoading(true);
    setError(null);
    setHasAsked(true);

    try {
      const messages: SolomonMessage[] = [{ role: "user", content: query }];

      const { data, error: invokeError } = await invokeEdgeFunction("solomon-chat", {
        body: {
          messages,
          context: {
            page: location.pathname,
            source: "search-inline",
            organizationId,
          },
        },
        policy: "background",
      });

      if (invokeError) throw invokeError;

      if (requestIdRef.current !== requestId) return;
      if (data?.content) {
        setResponse(data.content);
      } else {
        setError("Solomon returned an empty response.");
      }
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      console.error("Solomon error:", err);
      setError(solomonErrorMessage(err));
    } finally {
      if (requestIdRef.current === requestId) setIsLoading(false);
    }
  }, [query, location.pathname, organizationId, solomonEnabled]);

  // Debounce the ask when query changes
  useEffect(() => {
    if (!query.trim() || query.length < 3) {
      setResponse(null);
      setError(null);
      setHasAsked(false);
      return;
    }

    // Auto-ask after a short delay
    const timer = setTimeout(() => {
      if (!hasAsked) {
        askSolomon();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query, hasAsked, askSolomon]);

  // Reset state and invalidate in-flight replies when the query or tenant
  // changes, so an answer from one organization can never render in another.
  useEffect(() => {
    requestIdRef.current += 1;
    setResponse(null);
    setError(null);
    setHasAsked(false);
    setIsLoading(false);
  }, [organizationId, query]);

  const handleCopy = () => {
    if (response) {
      navigator.clipboard.writeText(response);
      setCopied(true);
      toast.success("Response copied to clipboard");
      setTimeout(() => setCopied(false), COPY_FEEDBACK_TIMEOUT_MS);
    }
  };

  const handleRetry = () => {
    setHasAsked(false);
    askSolomon();
  };

  // Solomon AI is a Flourish-tier feature — show an upgrade card for lower tiers.
  if (!solomonEnabled) {
    return (
      <div className={cn("p-4", className)}>
        <FeatureTierGate feature="solomon_ai">{null}</FeatureTierGate>
      </div>
    );
  }

  // Don't show anything if query is too short
  if (!query.trim() || query.length < 3) {
    return (
      <div className={cn("p-4", className)}>
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Ask Solomon anything</p>
            <p className="text-xs">Type your question after the ? prefix</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {getSolomonSuggestions(location.pathname).map((suggestion) => (
              <Button
                key={suggestion}
                variant="outline"
                size="sm"
                className="h-auto py-1.5 px-2.5 text-xs"
                onClick={() => onAsk?.(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border/40">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Ask Solomon
          </p>
          <p className="text-sm font-medium text-foreground truncate">{query}</p>
        </div>
      </div>

      {/* Response area */}
      <div className="flex-1 p-4 overflow-auto">
        {isLoading && (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Solomon is thinking...</span>
          </div>
        )}

        {error && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={handleRetry} className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {response && (
          <div className="space-y-3">
            <Markdown className="text-foreground">{response}</Markdown>

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={handleCopy}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={handleRetry}
              >
                <RefreshCw className="h-3 w-3" />
                Regenerate
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer - expand to panel */}
      <div className="p-3 border-t border-border/40 bg-muted/30">
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={onExpand}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Continue in full conversation
          <ExternalLink className="h-3 w-3 ml-auto" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Suggested questions based on current page context
 */
export function getSolomonSuggestions(pathname: string): string[] {
  if (pathname.startsWith("/finance") || pathname.startsWith("/contributions")) {
    return [
      "How do funds work?",
      "How do I split a contribution?",
      "Where are giving reports?",
    ];
  }

  if (pathname.startsWith("/requests")) {
    return [
      "How do I assign a request?",
      "What are request statuses?",
      "How to close a request?",
    ];
  }

  if (pathname.startsWith("/community") || pathname.startsWith("/people")) {
    return [
      "How do I add a person?",
      "How do households work?",
      "Where is the directory?",
    ];
  }

  if (pathname.startsWith("/tasks") || pathname.startsWith("/projects")) {
    return [
      "How do I create a task?",
      "How do projects work?",
      "How to track progress?",
    ];
  }

  if (pathname.startsWith("/calendar") || pathname.startsWith("/events")) {
    return [
      "How to record attendance?",
      "How do event registrations work?",
      "Where are event reports?",
    ];
  }

  return [
    "How do I find a person?",
    "Where are my tasks?",
    "How do I record a contribution?",
  ];
}
